import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth';
import type { CustomerOptionDto } from '../dto/reference';
import PresenceBanner from '../components/presence/PresenceBanner';
import { usePresence } from '../hooks/usePresence';
import { useScopedFeedback } from '../utils/feedback';
import { fetchSuggestedManualOrderNumber } from '../modules/orders/api/ordersApi';
import { getDisplayOrderNumber } from '../modules/orders/types';
import { getOrderProductSaveErrorMessage } from '../modules/orders/orderProductSaveErrors';
import { getDefaultCustomerPaymentDueDate } from '../modules/orders/customerPayment';
import {
  canChangeCustomerOrderStatus,
  canEditCustomerOrderAccountManager,
  canEditCustomerOrderDeliveryMode,
} from '../modules/orders/customerOrderAccessPolicy';
import { PAGE_SHELL_STYLE } from '../modules/orders/orderDetailLayout';
import { getOrderMarginVisibility } from '../modules/orders/process/orderMarginStage';
import OrderProcessHeader from '../modules/orders/process/OrderProcessHeader';
import OrderProcessRail from '../modules/orders/process/OrderProcessRail';
import OrderGuidePanel from '../modules/orders/process/guide/OrderGuidePanel';
import OrderStepGuide from '../modules/orders/process/guide/OrderStepGuide';
import { getOrderGuideDraftFactsFromDraft } from '../modules/orders/process/guide/orderGuideFacts';
import { getDraftGuideItems, getGuideHelpKey, getGuidePrimaryLabelKey } from '../modules/orders/process/guide/orderGuideRules';
import { getOrderProcessPosition } from '../modules/orders/process/orderProcessSteps';
import { getOrderStepBar } from '../modules/orders/process/orderStepBar';
import OrderDraftEditor from '../modules/orders/process/draft/OrderDraftEditor';
import { getOrderDraftReadiness, ORDER_DRAFT_HANDOVER_WARNING_KEY, ORDER_DRAFT_SAVE_ERROR_KEY } from '../modules/orders/process/draft/orderDraftModel';
import { useOrderDraftEditor } from '../modules/orders/process/draft/useOrderDraftEditor';
import { useBackNavigation } from '../hooks/useBackNavigation';
import './OrderDetail.css';
import '../modules/orders/process/OrderProcess.css';

const NEW_ORDER_POSITION = getOrderProcessPosition('NEW_INQUIRY');

/**
 * New customer order (owner 2026-10-05): the ZK card in step „Szkic”, the
 * same screen as a saved draft. The first „Zapisz szkic” / „Stwórz ofertę”
 * creates the order (`POST /orders`, status Szkic) and continues on its card
 * (`/app/orders/:id`); „Stwórz ofertę” opens the Oferta step there.
 */
const OrderNew: React.FC = () => {
  const { t } = useTranslation(['orders', 'common']);
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { modal } = App.useApp();
  const feedback = useScopedFeedback('zk-card');
  const currentUser = authService.getCurrentUser();
  const canViewSalePrices = authService.canViewCustomerSalePrices();
  const canEditSalePrices = authService.canEditCustomerSalePrices();
  const canEditAccountManager = canEditCustomerOrderAccountManager(currentUser);
  const canViewCustomerPayments = authService.canViewCustomerPayments();
  // Same predicates as the saved card; the backend still decides what it returns.
  const marginVisibility = getOrderMarginVisibility({
    canViewMargin: authService.canViewMargin(),
    canViewPlannedMargin: authService.canViewCustomerOrderPlannedMargin(),
  });
  const confirmation = useMemo(() => ({ modal, t }), [modal, t]);
  // Who may set which field of a NEW order. `POST /orders` takes the commercial header
  // (date, numbering, supplier, loading place…) from every creating role, as the retired
  // wizard offered it to all, so `commercial` is true here; „Dostawa” is the exception —
  // the follow-up update is limited by the commercial-fields policy.
  const rights = useMemo(() => ({
    commercial: true,
    accountManager: canEditAccountManager,
    customerPayments: canViewCustomerPayments,
    deliveryMode: canEditCustomerOrderDeliveryMode(currentUser),
  }), [canEditAccountManager, canViewCustomerPayments, currentUser]);
  // The save-error toast belongs to the attempt that raised it: it goes with the next edit
  // (a customer chosen, a product added or corrected) and with this screen.
  const toastsRef = useRef(feedback.message);
  toastsRef.current = feedback.message;
  const editor = useOrderDraftEditor({
    onEdit: () => toastsRef.current.destroy(ORDER_DRAFT_SAVE_ERROR_KEY),
    order: null,
    isNew: true,
    caps: { canViewSalePrices, canEditSalePrices, ...rights },
    confirmation,
  });
  const { draft, dirty, saving } = editor;
  useEffect(() => () => { toastsRef.current.destroy(ORDER_DRAFT_SAVE_ERROR_KEY); }, []);
  const [customer, setCustomer] = useState<CustomerOptionDto | undefined>();
  const [numberPreview, setNumberPreview] = useState<string | null>(null);
  const presenceOthers = usePresence('CUSTOMER_CREATE', draft.header.customerId || null);

  // The number the server will most likely give (scoped by the order date); the binding one comes with the save.
  const orderDateIso = draft.header.orderDate?.isValid() ? draft.header.orderDate.toISOString() : undefined;
  useEffect(() => {
    let ignore = false;
    fetchSuggestedManualOrderNumber(orderDateIso)
      .then((number) => { if (!ignore) setNumberPreview(number || null); })
      .catch(() => { if (!ignore) setNumberPreview(null); });
    return () => { ignore = true; };
  }, [orderDateIso]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const readiness = getOrderDraftReadiness(draft.lines, canViewSalePrices);
  const bar = getOrderStepBar('DRAFT', {
    status: 'NEW_INQUIRY',
    missing: [],
    linesWithoutPrice: readiness.linesWithoutPrice,
    lineCount: readiness.lineCount,
    draftPendingRow: Boolean(editor.pendingRow?.line),
    offerAccepted: false,
    offerAcceptanceDate: false,
    transportMissing: null,
    allIssued: false,
    anyIssued: false,
    can: { changeStatus: canChangeCustomerOrderStatus(currentUser), issueWz: false, salesInvoice: false },
  });

  // Steps 1–3 follow the first mockup: the step card carries the checklist and the buttons (no sticky bar).
  const guideItems = getDraftGuideItems(getOrderGuideDraftFactsFromDraft(draft, { canViewSalePrices, canOpenPrices: false }));
  const guideHint = bar.primaryDisabled || bar.hint.tone === 'warning' ? { tone: bar.hint.tone, text: t(bar.hint.key, bar.hint.params) } : null;

  const save = async (openOffer: boolean) => {
    feedback.clear();
    try {
      const result = await editor.save();
      if (result.status === 'invalid') {
        // One message for the first missing condition; it closes itself and on the next attempt.
        feedback.error(t(result.problemKey), { key: ORDER_DRAFT_SAVE_ERROR_KEY });
        return;
      }
      feedback.message.destroy(ORDER_DRAFT_SAVE_ERROR_KEY);
      if (result.status !== 'created') return;
      const orderNumber = getDisplayOrderNumber(result.order);
      feedback.success(t('orders:process.draft.created', { orderNumber }));
      if (result.deliveryModeFailed) {
        feedback.warning(t('orders:process.draft.deliveryModeNotSaved', { orderNumber }), { sticky: true, key: ORDER_DRAFT_HANDOVER_WARNING_KEY });
      }
      navigate(`/app/orders/${result.order.id}`, { replace: true, state: openOffer ? { zkStep: 'OFFER' } : undefined });
    } catch (error) {
      feedback.error(getOrderProductSaveErrorMessage(t, error, t('orders:messages.createError')), { key: ORDER_DRAFT_SAVE_ERROR_KEY });
    }
  };

  const leave = () => {
    if (!dirty) {
      goBack('/app/orders');
      return;
    }
    modal.confirm({
      title: t('orders:process.draft.leaveTitle'),
      content: t('orders:process.draft.leaveContent'),
      okText: t('orders:process.draft.leaveOk'),
      cancelText: t('common:cancel'),
      onOk: () => goBack('/app/orders'),
    });
  };

  const orderNumber = draft.header.useManualNumbering && draft.header.manualOrderNumber.trim()
    ? draft.header.manualOrderNumber.trim()
    : numberPreview && !draft.header.useManualNumbering
      ? t('orders:process.draft.numberPreview', { number: numberPreview })
      : t('orders:process.draft.numberPending');

  return (
    <div className="zk-page">
      <div style={PAGE_SHELL_STYLE} className="order-detail-page zk-process" data-testid="zk-new-order">
        <PresenceBanner scopeType="CUSTOMER_CREATE" others={presenceOthers} style={{ marginBottom: 16 }} />
        <OrderProcessHeader
          orderNumber={orderNumber}
          viewedStep="DRAFT"
          hideStepCopy
          pillKey="orders:statusOptions.NEW_INQUIRY"
          pillTone="default"
          customerName={customer?.name || '—'}
          createdAt={t('orders:process.draft.createdAtPending')}
          currency={draft.header.currency}
          accountManagerName={currentUser?.name || '—'}
          margin={null}
          marginVisibility={marginVisibility}
          marginPending="new"
          back={(
            <Button icon={<ArrowLeftOutlined />} onClick={leave}>{t('orders:process.back')}</Button>
          )}
        />
        <OrderProcessRail position={NEW_ORDER_POSITION} viewed="DRAFT" closed={false} lockAhead onView={() => undefined} />
        <OrderGuidePanel
          guide={(
            <OrderStepGuide
              step="DRAFT"
              helpKey={getGuideHelpKey('DRAFT', false)}
              items={guideItems}
              primary={bar.primary ? {
                labelKey: getGuidePrimaryLabelKey(bar.primary),
                disabled: bar.primaryDisabled,
                busy: saving,
                onClick: () => { void save(true); },
              } : null}
              secondary={(
                <Button size="large" icon={<SaveOutlined />} loading={saving} onClick={() => { void save(false); }} data-testid="zk-draft-save">
                  {t('orders:process.bar.saveDraft')}
                </Button>
              )}
              hint={guideHint}
            />
          )}
        />
        <OrderDraftEditor
          draft={draft}
          editor={editor}
          isNew
          canEditLines
          canViewSalePrices={canViewSalePrices}
          canEditSalePrices={canEditSalePrices}
          rights={rights}
          customerName={customer?.name || ''}
          customerAddress={customer?.address}
          onCustomerChange={(next) => {
            setCustomer(next);
            editor.setHeader({ customerPaymentDueDate: getDefaultCustomerPaymentDueDate(draft.header.orderDate, next) });
          }}
        />
      </div>
    </div>
  );
};

export default OrderNew;
